import React from 'react'
import { Formik, Form } from 'formik'
import { FormattedMessage } from 'react-intl'
import FormConfirmationBar from 'legacy/components/Forms/FormConfirmationBar'
import { hasTouchedFieldsWithErrors } from 'legacy/common/formikUtils'
import { ProductImage } from 'legacy/routes/catalog/products/product/form-components/ProductImage'
import { NewProductInfo } from 'legacy/routes/catalog/products/product/form-components/NewProductInfo'
import { ProductDescription } from 'legacy/routes/catalog/products/product/form-components/ProductDescription'
import { ProductCodes } from 'legacy/routes/catalog/products/product/form-components/ProductCodes'
import { ProductAttributes } from 'legacy/routes/catalog/products/product/form-components/ProductAttributes'
import { ProductPricing } from 'legacy/routes/catalog/products/product/form-components/ProductPricing'
import ProductAisle from 'legacy/routes/catalog/products/product/form-components/ProductAisle'
import { type NewProductFormValues } from 'legacy/routes/catalog/types/product.types'
import { isValidGTIN } from 'legacy/common/gtin'
import useAccessControl from 'legacy/components/AccessControl/useAccessControl'
import { Flipper } from 'legacy/contexts/flipper/FlipperContext'
import { placeholderId } from './NewProductContainer'

interface NewProductFormProps {
  product: NewProductFormValues
  onSubmit: (values: NewProductFormValues) => void
  isSubmitting: boolean
}

interface NewProductFormFlipperProps {
  showDepartmentAisle: boolean
}

interface FormErrors {
  [index: string]: string
}

const VALID_PRICE_PATTERN = /\d+\.\d\d/

class NewProductForm extends React.Component<NewProductFormProps & NewProductFormFlipperProps> {
  validate = (values: NewProductFormValues) => {
    const errors: FormErrors = {}
    const { showDepartmentAisle } = this.props

    if (!values.name) {
      errors.name = 'Please enter a name'
    }

    if (!values.costPricePerUnit.match(VALID_PRICE_PATTERN)) {
      errors.costPricePerUnit = 'Please include a price (e.g. 3.00 or 2.99)'
    }
    // validate deparment and aisle only if shown
    if (showDepartmentAisle && values.departmentId === placeholderId) {
      errors.departmentId = 'Please choose a primary department'
    }
    if (showDepartmentAisle && values.aisleId === placeholderId) {
      errors.aisleId = 'Please choose a primary aisle'
    }
    if (!(values.productCodes || values.retailerReferenceCode)) {
      errors.productCodes = 'Please enter a valid UPC or RRC'
    }

    if (values.productCodes && !isValidGTIN(values.productCodes)) {
      errors.productCodes = 'Please enter a valid UPC'
    }

    if (!values.brandName) {
      errors.brandName = 'Please enter a brand name'
    }

    return errors
  }

  render() {
    const { product, onSubmit, isSubmitting, showDepartmentAisle } = this.props

    return (
      <Formik
        initialValues={product}
        onSubmit={onSubmit}
        validate={this.validate}
        enableReinitialize
      >
        {formProps => (
          <Form>
            <NewProductInfo {...formProps} />
            <ProductPricing {...formProps} />
            <ProductDescription {...formProps} />
            <ProductCodes {...formProps} />
            {showDepartmentAisle && <ProductAisle {...formProps} />}
            <ProductAttributes {...formProps} />
            {formProps.dirty && (
              <FormConfirmationBar
                error={hasTouchedFieldsWithErrors(formProps.errors, formProps.touched)}
                errorMessage={<FormattedMessage id="catalog.product.form.errorMessage" />}
                isSubmitting={isSubmitting}
                submitButtonText={
                  <FormattedMessage id="catalog.product.requestNew.submitButtonText" />
                }
              />
            )}
          </Form>
        )}
      </Formik>
    )
  }
}

const withFlipper = (
  Component: React.ComponentClass<NewProductFormProps & NewProductFormFlipperProps>
) => {
  return (props: NewProductFormProps) => {
    const hasAccess = useAccessControl()
    const hideDepartmentAisle = hasAccess({
      warehouseFlippers: Flipper.ProductRequestHideDepartmentAisle,
    })

    return <Component showDepartmentAisle={!hideDepartmentAisle} {...props} />
  }
}

export default withFlipper(NewProductForm)
